// src/PlaytestPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";

function getImageSizeFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return 0;
  const parts = dataUrl.split(",");
  if (parts.length < 2) return 0;
  const base64String = parts[1];
  const paddingMatch = base64String.match(/=+$/);
  const padding = paddingMatch ? paddingMatch[0].length : 0;
  const base64Length = base64String.length - padding;
  return Math.floor((base64Length * 3) / 4);
}

function formatBytes(bytes) {
  if (!bytes || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}

// human-friendly time formatter (seconds -> up to 2 units: d, h, m, s)
function formatElapsed(seconds) {
  if (!Number.isFinite(seconds)) return "0s";
  const total = Math.max(0, Math.floor(seconds));
  const units = [
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
    ["s", 1],
  ];

  let remaining = total;
  const parts = [];

  for (const [label, unitSeconds] of units) {
    if (remaining >= unitSeconds) {
      const value = Math.floor(remaining / unitSeconds);
      remaining %= unitSeconds;
      parts.push(`${value}${label}`);
    }
    if (parts.length === 2) break;
  }

  if (!parts.length) return "0s";
  return parts.join(" ");
}

// Convert a data URL ("data:image/png;base64,...") to a Blob
async function dataUrlToBlob(dataUrl) {
  const res = await fetch(dataUrl);
  return await res.blob();
}

function PlaytestPage() {
  const iframeRef = useRef(null);
  const pendingScreenshotRef = useRef(null);
  const sessionPromiseRef = useRef(null);
  const nextChunkIndexRef = useRef(0);

  // Developer preview mode (owner viewing their own game):
  // - no session creation
  // - no usage consumption
  // - feedback actions disabled
  const [isDeveloperPreview, setIsDeveloperPreview] = useState(false);

  // Dynamic header display
  const [displayGameTitle, setDisplayGameTitle] = useState("");
  const [displayDevName, setDisplayDevName] = useState("");

  // When we *start counting* time for this session (ms)
  const [sessionStartMs, setSessionStartMs] = useState(null);
  const [isSessionClockRunning, setIsSessionClockRunning] = useState(false);

  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [currentNote, setCurrentNote] = useState("");
  const [currentTag, setCurrentTag] = useState("bug");
  const [formScreenshot, setFormScreenshot] = useState(null);
  const [entryUrl, setEntryUrl] = useState(null);
  const [resolving, setResolving] = useState(true);
  const [resolveError, setResolveError] = useState(null);
  const [accessGranted, setAccessGranted] = useState(false);
  const [gameMeta, setGameMeta] = useState(null);
  const [buildMeta, setBuildMeta] = useState(null);
  const [testerEmail, setTesterEmail] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [pendingInvite, setPendingInvite] = useState(null);
  const [debugResults, setDebugResults] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);

  const { currentUser, loading } = useAuth();

  // persistent session row (only created once the session REALLY starts)
  const [session, setSession] = useState(null);

  // quota / time-limit state
  const [usageLimitSeconds, setUsageLimitSeconds] = useState(null); // total allowed for this game/tester
  const [usageUsedSecondsBefore, setUsageUsedSecondsBefore] = useState(0); // already used before this session
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(null); // live countdown
  const [timeLimitReached, setTimeLimitReached] = useState(false);

  // game-ready signalling
  const [gameReady, setGameReady] = useState(false);
  const [waitingForGameReady, setWaitingForGameReady] = useState(false);

  const location = useLocation();

  const requiresReadySignal = !!(gameMeta && gameMeta.require_ready_signal);

  function getElapsedSeconds() {
    if (!sessionStartMs) return 0;
    return Math.round((Date.now() - sessionStartMs) / 1000);
  }

  const deriveGameTitle = (g) => {
    return (
      g?.title ||
      g?.name ||
      g?.game_title ||
      g?.display_name ||
      g?.slug ||
      "Untitled game"
    );
  };

  const fetchOwnerDisplayName = async (ownerId) => {
    if (!ownerId) return null;

    // Best-effort: many projects use `profiles` keyed by auth.users.id.
    // If this table/columns don't exist, we fail safely.
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("display_name, full_name, username")
        .eq("id", ownerId)
        .maybeSingle();

      if (error) return null;

      return data?.display_name || data?.full_name || data?.username || null;
    } catch {
      return null;
    }
  };

  const setHeaderFromGameRow = async (gameRow, { ownerViewing } = {}) => {
    try {
      setDisplayGameTitle(deriveGameTitle(gameRow));

      const ownerNameCandidate =
        gameRow?.owner_name ||
        gameRow?.developer_name ||
        gameRow?.dev_name ||
        gameRow?.creator_name ||
        null;

      const ownerName =
        ownerNameCandidate || (await fetchOwnerDisplayName(gameRow?.owner_id));

      // IMPORTANT: no email fallback — if we don't have a name, omit the BY line
      setDisplayDevName(ownerName || null);
    } catch {
      setDisplayGameTitle(deriveGameTitle(gameRow));
      setDisplayDevName(null);
    }
  };


  // --- Helper to send messages into the iframe ---
  function sendMessageToGame(message) {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(message, "*");
  }

  function startRecordingIfPossible(sessionOverride) {
    const sess = sessionOverride || session;
    if (!sess || !sess.id) return;
    nextChunkIndexRef.current = 0;
    sendMessageToGame({
      type: "PLAYTEST_START_RECORDING",
      sessionId: sess.id,
      timesliceMs: 5000, // 5s chunks
    });
  }

  function stopRecordingIfPossible() {
    const sess = session;
    if (!sess || !sess.id) return;
    sendMessageToGame({
      type: "PLAYTEST_STOP_RECORDING",
      sessionId: sess.id,
    });
  }

  function startSessionClock() {
    if (!sessionStartMs) {
      setSessionStartMs(Date.now());
    }
    setIsSessionClockRunning(true);
  }

  function stopSessionClock() {
    setIsSessionClockRunning(false);
  }

  // Inline accept/decline for pending invite on this page
  const inlineAcceptInvite = async () => {
    if (!pendingInvite) return;
    try {
      const acceptedAt = new Date().toISOString();

      const { error: iErr } = await supabase
        .from("invites")
        .update({ accepted_at: acceptedAt })
        .eq("id", pendingInvite.id);

      if (iErr) throw iErr;

      const { error: tErr } = await supabase
        .from("testers")
        .upsert(
          { game_id: pendingInvite.game_id, email: pendingInvite.email },
          { onConflict: ["game_id", "email"] }
        );

      if (tErr) throw tErr;

      setPendingInvite({ ...pendingInvite, accepted_at: acceptedAt });
      setAccessGranted(true);
    } catch (e) {
      console.error("Failed to accept invite inline:", e);
      setResolveError(
        "Failed to accept invite. Please try again or use the Tester Dashboard."
      );
    }
  };

  const inlineDeclineInvite = async () => {
    if (!pendingInvite) return;
    try {
      const { error: iErr } = await supabase
        .from("invites")
        .delete()
        .eq("id", pendingInvite.id);

      if (iErr) throw iErr;

      const { error: tErr } = await supabase
        .from("testers")
        .delete()
        .eq("game_id", pendingInvite.game_id)
        .eq("email", pendingInvite.email);

      if (tErr) console.warn("Failed to remove tester on decline:", tErr);

      setPendingInvite(null);
      setAccessGranted(false);
      setResolveError("Invite declined. You no longer have access to this game.");
    } catch (e) {
      console.error("Failed to decline invite inline:", e);
      setResolveError(
        "Failed to decline invite inline. Please try again from the Tester Dashboard."
      );
    }
  };

  // Resolve entry URL + gating
  useEffect(() => {
    if (loading) return;

    (async () => {
      setResolving(true);
      setResolveError(null);
      setAccessGranted(false);
      setIsDeveloperPreview(false);
      setInviteInfo(null);
      setPendingInvite(null);
      setGameMeta(null);
      setBuildMeta(null);
      setSession(null);
      setIframeReady(false);
      setUsageLimitSeconds(null);
      setUsageUsedSecondsBefore(0);
      setTimeRemainingSeconds(null);
      setTimeLimitReached(false);
      setSessionStartMs(null);
      setIsSessionClockRunning(false);
      setGameReady(false);
      setWaitingForGameReady(false);
      setEvents([]);
      setShowForm(false);
      setCurrentNote("");
      setCurrentTag("bug");
      setFormScreenshot(null);
      setDisplayGameTitle("");
      setDisplayDevName("");

      try {
        const q = new URLSearchParams(location.search);
        const buildId = q.get("build");
        const gameId = q.get("game");
        const inviteToken = q.get("invite");

        // 1) Invite-token flow: /play?invite=TOKEN
        if (inviteToken) {
          const { data: inv, error: invErr } = await supabase
            .from("invites")
            .select("*")
            .eq("token", inviteToken)
            .single();

          if (invErr || !inv) {
            throw new Error("Invalid invite token");
          }

          setInviteInfo(inv);

          const { data: gdata, error: gErr } = await supabase
            .from("games")
            .select("*")
            .eq("id", inv.game_id)
            .single();

          if (gErr || !gdata) throw gErr || new Error("Game not found");

          const ownerViewing = !!(
            currentUser?.id && currentUser.id === gdata.owner_id
          );
          setIsDeveloperPreview(ownerViewing);

          await setHeaderFromGameRow(gdata, { ownerViewing });

          setGameMeta({
            id: gdata.id,
            owner_id: gdata.owner_id,
            accessible_to_everyone: gdata.accessible_to_everyone,
            current_build_id: gdata.current_build_id,
            require_ready_signal: gdata.require_ready_signal,
          });

          let chosenBuild = null;

          if (gdata.current_build_id) {
            const { data: bdata, error: bErr } = await supabase
              .from("builds")
              .select("id, entry_url, game_id, status, uploaded_at")
              .eq("id", gdata.current_build_id)
              .single();
            if (bErr || !bdata)
              throw bErr || new Error("Current build not found");
            chosenBuild = bdata;
          } else {
            const { data: latest, error: lErr } = await supabase
              .from("builds")
              .select("id, entry_url, game_id, status, uploaded_at")
              .eq("game_id", inv.game_id)
              .eq("status", "Live")
              .order("uploaded_at", { ascending: false })
              .limit(1)
              .single();

            if (lErr || !latest)
              throw lErr || new Error("No live build for this game");
            chosenBuild = latest;
          }

          setEntryUrl(chosenBuild.entry_url);
          setBuildMeta(chosenBuild);

          // Auto-accept this invite & whitelist the email
          const acceptedAt = new Date().toISOString();
          try {
            await supabase
              .from("invites")
              .update({ accepted_at: acceptedAt })
              .eq("id", inv.id);

            await supabase
              .from("testers")
              .upsert(
                { game_id: inv.game_id, email: inv.email },
                { onConflict: ["game_id", "email"] }
              );
          } catch (e) {
            console.warn("Failed to persist invite acceptance:", e);
          }

          setPendingInvite({ ...inv, accepted_at: acceptedAt });
          setAccessGranted(true);
          return;
        }

        // 2) Direct /play?build=... -> debug / direct link
        if (buildId) {
          const { data, error } = await supabase
            .from("builds")
            .select("id, entry_url, game_id, status, uploaded_at, games(*)")
            .eq("id", buildId)
            .single();

          if (error || !data) throw error || new Error("Build not found");

          setEntryUrl(data.entry_url);
          setBuildMeta({
            id: data.id,
            game_id: data.game_id,
            status: data.status,
            uploaded_at: data.uploaded_at,
          });

          if (data.games) {
            const ownerViewing = !!(
              currentUser?.id && currentUser.id === data.games.owner_id
            );
            setIsDeveloperPreview(ownerViewing);

            await setHeaderFromGameRow(data.games, { ownerViewing });

            setGameMeta({
              id: data.games.id,
              owner_id: data.games.owner_id,
              accessible_to_everyone: data.games.accessible_to_everyone,
              current_build_id: data.id,
              require_ready_signal: data.games.require_ready_signal,
            });

            if (data.games.accessible_to_everyone) {
              setAccessGranted(true);
              return;
            }
            if (ownerViewing) {
              setAccessGranted(true);
              return;
            }
          }
          // Otherwise gated -> overlay
          return;
        }

        // 3) /play?game=... -> main shareable link with gating
        if (gameId) {
          const { data: gdata, error: gErr } = await supabase
            .from("games")
            .select("*")
            .eq("id", gameId)
            .single();

          if (gErr || !gdata) throw gErr || new Error("Game not found");

          const ownerViewing = !!(
            currentUser?.id && currentUser.id === gdata.owner_id
          );
          setIsDeveloperPreview(ownerViewing);

          await setHeaderFromGameRow(gdata, { ownerViewing });

          setGameMeta({
            id: gdata.id,
            owner_id: gdata.owner_id,
            accessible_to_everyone: gdata.accessible_to_everyone,
            current_build_id: gdata.current_build_id,
            require_ready_signal: gdata.require_ready_signal,
          });

          let chosenBuild = null;

          if (gdata.current_build_id) {
            const { data: bdata, error: bErr } = await supabase
              .from("builds")
              .select("id, entry_url, game_id, status, uploaded_at")
              .eq("id", gdata.current_build_id)
              .single();
            if (bErr || !bdata)
              throw bErr || new Error("Current build not found");
            chosenBuild = bdata;
          } else {
            const { data: latest, error: lErr } = await supabase
              .from("builds")
              .select("id, entry_url, game_id, status, uploaded_at")
              .eq("game_id", gameId)
              .eq("status", "Live")
              .order("uploaded_at", { ascending: false })
              .limit(1)
              .single();
            if (lErr || !latest)
              throw lErr || new Error("No live build for this game");
            chosenBuild = latest;
          }

          setEntryUrl(chosenBuild.entry_url);
          setBuildMeta(chosenBuild);

          // Public game? everyone can play
          if (gdata.accessible_to_everyone) {
            setAccessGranted(true);
            return;
          }

          // Owner always allowed
          if (ownerViewing) {
            setAccessGranted(true);
            return;
          }

          // Private game: need accepted tester
          if (!currentUser || !currentUser.email) {
            // Not an error, just gated -> show overlay
            return;
          }

          // Check testers whitelist
          const { data: tdata, error: tErr } = await supabase
            .from("testers")
            .select("*")
            .eq("game_id", gameId)
            .eq("email", currentUser.email)
            .maybeSingle();

          if (tErr) {
            console.warn("Tester check error:", tErr);
          }

          if (tdata) {
            setAccessGranted(true);
            return;
          }

          // Check invites (maybe accepted but no testers row yet)
          const { data: inv, error: iErr } = await supabase
            .from("invites")
            .select("*")
            .eq("game_id", gameId)
            .eq("email", currentUser.email)
            .maybeSingle();

          if (iErr) {
            console.warn("Invite check error:", iErr);
          }

          if (inv && inv.accepted_at) {
            try {
              await supabase
                .from("testers")
                .upsert(
                  { game_id: inv.game_id, email: inv.email },
                  { onConflict: ["game_id", "email"] }
                );
            } catch (e) {
              console.warn("Failed to sync testers from invite:", e);
            }
            setAccessGranted(true);
            return;
          }

          if (inv && !inv.accepted_at) {
            setPendingInvite(inv);
            return;
          }

          setResolveError("You are not invited to test this game.");
          return;
        }

        setResolveError("No build or game specified in URL");
      } catch (e) {
        console.error("Failed to resolve play entry URL:", e);
        setResolveError(String(e && e.message ? e.message : e));
      } finally {
        setResolving(false);
      }
    })();
  }, [
    location.search,
    loading,
    currentUser && currentUser.id,
    currentUser && currentUser.email,
  ]);

  // Mark session ended when leaving / closing (best effort) + stop recording
  useEffect(() => {
    if (!session || !session.id) return;

    let ended = false;

    const markEnded = async () => {
      if (ended) return;
      ended = true;
      try {
        await supabase
          .from("sessions")
          .update({ ended_at: new Date().toISOString() })
          .eq("id", session.id)
          .is("ended_at", null);
      } catch (e) {
        console.warn("Failed to mark session ended:", e);
      }
    };

    const handleBeforeUnload = () => {
      stopRecordingIfPossible();
      markEnded();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopRecordingIfPossible();
        markEnded();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopRecordingIfPossible();
      markEnded();
    };
  }, [session?.id]);

  const submitTesterEmail = async () => {
    const q = new URLSearchParams(location.search);
    const gameId = q.get("game");
    if (!gameId) return setResolveError("No game specified");
    try {
      const { data: inv, error: invErr } = await supabase
        .from("invites")
        .select("*")
        .eq("game_id", gameId)
        .eq("email", testerEmail)
        .single();
      if (inv && !invErr) {
        try {
          await supabase
            .from("invites")
            .update({ accepted_at: new Date().toISOString() })
            .eq("id", inv.id);
        } catch (e) { }
        setAccessGranted(true);
        return;
      }
      const { data: t, error: tErr } = await supabase
        .from("testers")
        .select("*")
        .eq("game_id", gameId)
        .eq("email", testerEmail)
        .single();
      if (t && !tErr) {
        setAccessGranted(true);
        return;
      }
      setResolveError("Email not invited for this game");
    } catch (e) {
      console.error("Error verifying tester email", e);
      setResolveError("Error verifying email");
    }
  };

  // Debug helper
  const runAccessDebug = async () => {
    setDebugLoading(true);
    setDebugError(null);
    try {
      const q = new URLSearchParams(location.search);
      const gameId = q.get("game");
      const buildId = q.get("build");
      const inviteToken = q.get("invite");

      const out = {};
      out.currentUser = currentUser || null;

      if (gameId) {
        const { data: game, error: gErr } = await supabase
          .from("games")
          .select("*")
          .eq("id", gameId)
          .single();
        out.game = {
          data: game || null,
          error: gErr ? String((gErr && gErr.message) || gErr) : null,
        };

        const { data: testers, error: tErr } = await supabase
          .from("testers")
          .select("*")
          .eq("game_id", gameId)
          .eq("email", currentUser && currentUser.email);
        out.testers = {
          data: testers || null,
          error: tErr ? String((tErr && tErr.message) || tErr) : null,
        };

        const { data: invites, error: iErr } = await supabase
          .from("invites")
          .select("*")
          .eq("game_id", gameId)
          .eq("email", currentUser && currentUser.email);
        out.invites = {
          data: invites || null,
          error: iErr ? String((iErr && iErr.message) || iErr) : null,
        };

        const { data: builds, error: bErr } = await supabase
          .from("builds")
          .select("*")
          .eq("game_id", gameId)
          .order("uploaded_at", { ascending: false })
          .limit(5);
        out.builds = {
          data: builds || null,
          error: bErr ? String((bErr && bErr.message) || bErr) : null,
        };
      }

      if (buildId) {
        const { data: build, error: buildErr } = await supabase
          .from("builds")
          .select("*")
          .eq("id", buildId)
          .single();
        out.build = {
          data: build || null,
          error: buildErr
            ? String((buildErr && buildErr.message) || buildErr)
            : null,
        };
      }

      if (inviteToken) {
        const { data: inv, error: invErr } = await supabase
          .from("invites")
          .select("*")
          .eq("token", inviteToken)
          .single();
        out.inviteToken = {
          data: inv || null,
          error: invErr ? String((invErr && invErr.message) || invErr) : null,
        };
      }

      if (session) out.session = session;

      setDebugResults(out);
    } catch (e) {
      setDebugError(String((e && e.message) || e));
      setDebugResults(null);
    } finally {
      setDebugLoading(false);
    }
  };

  const copyDebugResults = async () => {
    if (!debugResults) return;
    try {
      const text = JSON.stringify(debugResults, null, 2);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        alert("Copied debug JSON to clipboard");
      } else {
        const t = document.createElement("textarea");
        t.value = text;
        document.body.appendChild(t);
        t.select();
        document.execCommand("copy");
        document.body.removeChild(t);
        alert("Copied debug JSON to clipboard");
      }
    } catch (e) {
      console.error("Copy failed", e);
      alert("Copy failed: " + String((e && e.message) || e));
    }
  };

  const downloadDebugResults = () => {
    if (!debugResults) return;
    const blob = new Blob([JSON.stringify(debugResults, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `play-debug-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Live countdown + hard stop when limit is reached
  useEffect(() => {
    if (
      !usageLimitSeconds ||
      !sessionStartMs ||
      !isSessionClockRunning ||
      timeLimitReached
    )
      return;

    const interval = setInterval(() => {
      const elapsedThisSession = getElapsedSeconds();
      const usedTotal = usageUsedSecondsBefore + elapsedThisSession;
      const remaining = Math.max(0, usageLimitSeconds - usedTotal);

      setTimeRemainingSeconds(remaining);

      if (remaining <= 0 && !timeLimitReached) {
        setTimeLimitReached(true);
        stopSessionClock();

        // Stop recording & pause game
        stopRecordingIfPossible();
        pauseGame();

        // Mark session ended on the server so usage view is correct
        if (session && session.id) {
          supabase
            .from("sessions")
            .update({ ended_at: new Date().toISOString() })
            .eq("id", session.id)
            .then(({ error }) => {
              if (error) {
                console.warn(
                  "Failed to mark session ended on time limit:",
                  error
                );
              }
            });
        }
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    usageLimitSeconds,
    usageUsedSecondsBefore,
    sessionStartMs,
    isSessionClockRunning,
    timeLimitReached,
    session && session.id,
  ]);

  // --- Core: create or reuse session, but ONLY when we really start ---

  const getOrCreateSession = async () => {
    // Owner preview should never create/reuse a session or query usage.
    if (isDeveloperPreview) return null;

    if (session) return session;
    if (sessionPromiseRef.current) {
      return sessionPromiseRef.current;
    }

    const q = new URLSearchParams(location.search);
    const gameIdFromQuery = q.get("game");
    const buildIdFromQuery = q.get("build");

    // Prefer IDs that come directly from the chosen build first
    const gameId =
      (buildMeta && buildMeta.game_id) ||
      (gameMeta && gameMeta.id) ||
      (inviteInfo && inviteInfo.game_id) ||
      gameIdFromQuery ||
      null;

    const buildId =
      (buildMeta && buildMeta.id) ||
      buildIdFromQuery ||
      (gameMeta && gameMeta.current_build_id) ||
      null;

    if (!gameId) {
      console.warn("Cannot create session: missing game id", {
        gameIdFromQuery,
        gameMeta,
        buildMeta,
        inviteInfo,
      });
      return null;
    }

    if (!buildId) {
      console.warn("Cannot create session: missing build id", {
        buildIdFromQuery,
        gameMeta,
        buildMeta,
        inviteInfo,
      });
      return null;
    }

    const testerEmailForSession =
      (currentUser && currentUser.email) ||
      (inviteInfo && inviteInfo.email) ||
      null;

    // Check usage from v_usage
    let secondsLimit = null;
    let secondsUsed = 0;

    if (gameId && testerEmailForSession) {
      try {
        const { data: usageRow, error: usageErr } = await supabase
          .from("v_usage")
          .select("seconds_limit, seconds_used")
          .eq("game_id", gameId)
          .eq("email", testerEmailForSession)
          .maybeSingle();

        if (usageErr) {
          console.warn("Usage view error:", usageErr);
        } else if (usageRow) {
          secondsLimit = usageRow.seconds_limit;
          secondsUsed = usageRow.seconds_used || 0;

          if (secondsLimit != null && secondsUsed >= secondsLimit) {
            setResolveError(
              "You have used all of your allocated time for this playtest."
            );
            setAccessGranted(false);
            return null;
          }
        }
      } catch (e) {
        console.warn("Unexpected error checking usage:", e);
      }
    }

    const promise = (async () => {
      try {
        // 1) Try to reuse an existing *open* session for this tester/game/build
        let existing = null;
        try {
          let query = supabase
            .from("sessions")
            .select("*")
            .eq("game_id", gameId)
            .eq("build_id", buildId)
            .is("ended_at", null);

          if (testerEmailForSession) {
            query = query.eq("tester_email", testerEmailForSession);
          }

          const { data: openSession, error: openErr } = await query
            .order("started_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (openErr) {
            console.warn("Error checking open session:", openErr);
          } else if (openSession) {
            existing = openSession;
          }
        } catch (e) {
          console.warn("Unexpected error looking for existing session:", e);
        }

        if (existing) {
          setSession(existing);

          // initialise quota state for this session
          if (secondsLimit != null) {
            const remainingNow = Math.max(0, secondsLimit - secondsUsed);
            setUsageLimitSeconds(secondsLimit);
            setUsageUsedSecondsBefore(secondsUsed);
            setTimeRemainingSeconds(remainingNow);
            setTimeLimitReached(false);
          } else {
            setUsageLimitSeconds(null);
            setUsageUsedSecondsBefore(0);
            setTimeRemainingSeconds(null);
            setTimeLimitReached(false);
          }

          return existing;
        }

        // 2) Otherwise create a brand new session with started_at = real start
        const payload = {
          game_id: gameId,
          build_id: buildId,
          tester_email: testerEmailForSession,
          started_at: new Date().toISOString(),
        };
        const { data, error } = await supabase
          .from("sessions")
          .insert(payload)
          .select()
          .single();
        if (error) {
          console.error("Failed to create session:", error);
          return null;
        }
        setSession(data);

        // initialise quota state for this session
        if (secondsLimit != null) {
          const remainingNow = Math.max(0, secondsLimit - secondsUsed);
          setUsageLimitSeconds(secondsLimit);
          setUsageUsedSecondsBefore(secondsUsed);
          setTimeRemainingSeconds(remainingNow);
          setTimeLimitReached(false);
        } else {
          setUsageLimitSeconds(null);
          setUsageUsedSecondsBefore(0);
          setTimeRemainingSeconds(null);
          setTimeLimitReached(false);
        }

        return data;
      } catch (e) {
        console.error("Unexpected error creating session:", e);
        return null;
      } finally {
        sessionPromiseRef.current = null;
      }
    })();

    sessionPromiseRef.current = promise;
    return promise;
  };

  // Ensure we only start the session + recording ONCE, when the game is actually ready to play
  async function ensureSessionStarted() {
    if (isDeveloperPreview) return;
    if (timeLimitReached) return;
    if (isSessionClockRunning) return;

    const activeSession = await getOrCreateSession();
    if (!activeSession) return;

    // Start local clock + recording in sync with DB session.started_at
    setSessionStartMs(Date.now());
    setIsSessionClockRunning(true);
    startRecordingIfPossible(activeSession);
  }

  // Append event locally + (optionally) persist to Supabase as tester event
  const appendEvent = (ev, { persist = false } = {}) => {
    setEvents((prev) => [...prev, ev]);
    if (!persist) return;

    (async () => {
      const activeSession = await getOrCreateSession();
      if (!activeSession) return;

      // Upload screenshot to private R2 if present
      let screenshotKey = null;
      let screenshotContentType = null;
      let screenshotSizeBytes = null;

      if (ev.screenshot) {
        try {
          const blob = await dataUrlToBlob(ev.screenshot);
          screenshotContentType = blob.type || "image/png";
          screenshotSizeBytes =
            blob.size || getImageSizeFromDataUrl(ev.screenshot);

          const formData = new FormData();
          formData.append("session_id", activeSession.id);
          formData.append("event_id", String(ev.id || ""));
          formData.append(
            "file",
            blob,
            `screenshot-${activeSession.id}-${Date.now()}.png`
          );

          const { data: up, error: upErr } = await supabase.functions.invoke(
            "upload-screenshot",
            { body: formData }
          );

          if (upErr || !up?.ok || !up?.key) {
            console.warn("Screenshot upload failed:", upErr, up);
          } else {
            screenshotKey = up.key;
            screenshotSizeBytes = up.size_bytes ?? screenshotSizeBytes;
            screenshotContentType = up.content_type ?? screenshotContentType;
          }
        } catch (e) {
          console.warn("Screenshot upload exception:", e);
        }
      }

      const row = {
        session_id: activeSession.id,
        direction: "tester",
        type: "feedback",
        note: ev.note || "",
        payload: {
          tag: ev.tag != null ? ev.tag : null,
          time_seconds: ev.timeSeconds != null ? ev.timeSeconds : null,
          screenshot_key: screenshotKey,
          screenshot_content_type: screenshotContentType,
          screenshot_size_bytes: screenshotSizeBytes,
        },
      };

      const { error } = await supabase.from("session_events").insert(row);
      if (error) {
        console.error("Failed to persist session event:", error);
      }
    })().catch((e) =>
      console.error("Unexpected error in appendEvent persist:", e)
    );
  };

  // Listen for dev -> tester messages in real time
  useEffect(() => {
    if (!session || !session.id) return;

    const channel = supabase
      .channel(`session-${session.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "session_events",
          filter: `session_id=eq.${session.id}`,
        },
        (payload) => {
          const ev = payload.new;
          if (!ev || ev.direction !== "dev") return; // only dev messages

          const meta = ev.payload || {};

          // Fallback: if time_seconds isn't in payload, derive from timestamps
          let timeSeconds = 0;
          if (typeof meta.time_seconds === "number") {
            timeSeconds = meta.time_seconds;
          } else if (session.started_at && ev.created_at) {
            const startedMs = new Date(session.started_at).getTime();
            const createdMs = new Date(ev.created_at).getTime();
            timeSeconds = Math.max(
              0,
              Math.floor((createdMs - startedMs) / 1000)
            );
          }

          const newEvent = {
            id: ev.id,
            timeSeconds,
            tag: meta.tag || "Dev message",
            direction: "dev",
            type: ev.type || "message",
            note: ev.note || "",
            isDevMessage: true,
            // no screenshot displayed for dev messages here
          };

          // Don't persist again (this row already exists in DB)
          appendEvent(newEvent, { persist: false });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session && session.id, session && session.started_at]);

  // Try to focus the Godot canvas inside the iframe
  function focusGameCanvas() {
    const iframe = iframeRef.current;
    if (!iframe) return;

    window.requestAnimationFrame(() => {
      try {
        const iframeWindow = iframe.contentWindow;
        if (!iframeWindow || !iframeWindow.document) {
          if (typeof iframe.focus === "function") {
            iframe.focus();
          }
          return;
        }

        const doc = iframeWindow.document;
        const canvas = doc.querySelector("canvas");

        if (canvas && typeof canvas.focus === "function") {
          canvas.focus();
        } else if (typeof iframeWindow.focus === "function") {
          iframeWindow.focus();
        } else if (typeof iframe.focus === "function") {
          iframe.focus();
        }
      } catch (err) {
        if (typeof iframe.focus === "function") {
          iframe.focus();
        }
      }
    });
  }

  function pauseGame() {
    sendMessageToGame({ type: "PLAYTEST_PAUSE" });
  }

  function resumeGame() {
    sendMessageToGame({ type: "PLAYTEST_RESUME" });
  }

  function captureScreenshotForContext(context) {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      pendingScreenshotRef.current = context;
      iframe.contentWindow.postMessage(
        { type: "PLAYTEST_CAPTURE_SCREENSHOT" },
        "*"
      );
      focusGameCanvas();
    }
  }

  function openFeedbackForm() {
    setShowForm(true);
    setFormScreenshot(null);
    pauseGame();
    captureScreenshotForContext({ mode: "form" });
  }

  function openQuickFeedback(tag) {
    setCurrentTag(tag);
    setShowForm(true);
    setFormScreenshot(null);
    pauseGame();
    captureScreenshotForContext({ mode: "form" });
  }

  function closeFeedbackForm() {
    resumeGame();
    setShowForm(false);
    setCurrentNote("");
    setCurrentTag("bug");
    setFormScreenshot(null);
    focusGameCanvas();
  }

  // Block F8 while waiting for game ready (if required) / dev preview
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "F8") {
        e.preventDefault();
        if (
          isDeveloperPreview ||
          !accessGranted ||
          timeLimitReached ||
          (requiresReadySignal && !gameReady)
        )
          return;
        openFeedbackForm();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    isDeveloperPreview,
    accessGranted,
    timeLimitReached,
    requiresReadySignal,
    gameReady,
  ]);

  // Handle incoming screenshots + recording chunks + game-ready from the game iframe
  useEffect(() => {
    function handleMessage(event) {
      const data = event.data;
      if (!data || !data.type) return;

      console.log("[Playtest] message from iframe:", data.type, data);

      // 0) Game signals it is ready to start the session
      if (data.type === "PLAYSET_GAME_READY") {
        if (requiresReadySignal) {
          setGameReady(true);
          setWaitingForGameReady(false);

          // Start the session + recording now that the game is ready (testers only)
          if (!isDeveloperPreview) {
            ensureSessionStarted();
          }
          resumeGame();
          focusGameCanvas();
        }
        return;
      }

      // 1) Recording chunks from iframe -> upload to Supabase function
      if (data.type === "PLAYSET_RECORDING_CHUNK") {
        if (!session || !session.id || data.sessionId !== session.id) return;

        const chunk = data.chunk;

        // Be a bit forgiving: just check it quacks like a Blob
        if (!chunk || typeof chunk.arrayBuffer !== "function") {
          console.warn("[Playtest] Chunk is not a Blob-like object", chunk);
          return;
        }

        const index =
          typeof data.chunkIndex === "number"
            ? data.chunkIndex
            : nextChunkIndexRef.current++;

        const isFinal = !!data.isFinal;

        (async () => {
          try {
            const formData = new FormData();
            formData.append("session_id", session.id);
            formData.append("chunk_index", String(index));
            formData.append("is_final", isFinal ? "true" : "false");
            // Name is mostly cosmetic – helps in R2 logs
            formData.append("file", chunk, `chunk-${index}.webm`);

            const { data: fnData, error } = await supabase.functions.invoke(
              "record-chunk",
              {
                body: formData,
              }
            );

            if (error) {
              console.error("Failed to upload recording chunk:", error, fnData);
            }
          } catch (err) {
            console.error("Unexpected error uploading recording chunk:", err);
          }
        })();

        return;
      }

      // 2) Screenshots from iframe -> upload on persist (appendEvent does it)
      if (data.type === "PLAYTEST_SCREENSHOT") {
        const context = pendingScreenshotRef.current;
        const dataUrl = data.dataUrl;

        // Quick reaction tag (Fun, Boring, Love, Hate, etc.)
        if (context && context.mode === "quickTag") {
          const newEvent = {
            id: Date.now(),
            timeSeconds: context.timeSeconds,
            tag: context.tag,
            note: "",
            screenshot: dataUrl,
          };
          appendEvent(newEvent, { persist: true });
          pendingScreenshotRef.current = null;
          return;
        }

        // Plain screenshot button
        if (context && context.mode === "screenshot") {
          const newEvent = {
            id: Date.now(),
            timeSeconds: context.timeSeconds,
            tag: "📸 Screenshot",
            note: "",
            screenshot: dataUrl,
          };
          appendEvent(newEvent, { persist: true });
          pendingScreenshotRef.current = null;
          return;
        }

        // Feedback form – screenshot only, event saved on submit
        if (context && context.mode === "form") {
          setFormScreenshot(dataUrl);
          pendingScreenshotRef.current = null;
          return;
        }

        // Fallback: no context - treat as standard screenshot
        const t = getElapsedSeconds();
        const newEvent = {
          id: Date.now(),
          timeSeconds: t,
          tag: "screenshot",
          note: "Screenshot",
          screenshot: dataUrl,
        };
        appendEvent(newEvent, { persist: true });
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionStartMs, requiresReadySignal, gameReady, isDeveloperPreview]);

  // When iframe is ready:
  //  - If game does NOT require ready signal => start session + recording here
  //  - If game DOES require ready signal => show "waiting" state until PLAYSET_GAME_READY
  useEffect(() => {
    if (!iframeReady || !accessGranted || !entryUrl) return;
    if (timeLimitReached) return;

    if (!isDeveloperPreview && !requiresReadySignal && !isSessionClockRunning) {
      ensureSessionStarted();
    }

    if (
      !isDeveloperPreview &&
      requiresReadySignal &&
      !gameReady &&
      !timeLimitReached
    ) {
      setWaitingForGameReady(true);
      // Clock & recording will start once we receive PLAYSET_GAME_READY
    }
  }, [
    iframeReady,
    accessGranted,
    entryUrl,
    requiresReadySignal,
    gameReady,
    timeLimitReached,
    isSessionClockRunning,
    isDeveloperPreview,
  ]);

  function handleSubmitFeedback(e) {
    e.preventDefault();
    const t = getElapsedSeconds();
    const newEvent = {
      id: Date.now(),
      timeSeconds: t,
      note: currentNote.trim(),
      tag: currentTag,
    };

    if (formScreenshot) {
      newEvent.screenshot = formScreenshot;
    }

    appendEvent(newEvent, { persist: true });
    closeFeedbackForm();
  }

  const actionsDisabled =
    isDeveloperPreview ||
    !accessGranted ||
    timeLimitReached ||
    (requiresReadySignal && !gameReady && !timeLimitReached);

  function addQuickTag(tag) {
    if (actionsDisabled) return;
    const t = getElapsedSeconds();
    captureScreenshotForContext({ mode: "quickTag", tag, timeSeconds: t });
  }

  function requestScreenshot() {
    if (actionsDisabled) return;
    const t = getElapsedSeconds();
    captureScreenshotForContext({ mode: "screenshot", timeSeconds: t });
  }

  // Local mock-only dev message for marketing/demo – NOT persisted
  function addDevMessage() {
    if (actionsDisabled) return;
    const t = getElapsedSeconds();
    const newEvent = {
      id: Date.now(),
      timeSeconds: t,
      tag: "dev-message",
      note: "Hi, this is a message from the dev!",
      isDevMessage: true,
    };
    appendEvent(newEvent, { persist: false });
  }

  return (
    <div className="h-screen flex flex-col bg-[#111] text-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 bg-white/5">
        <Link to="/">
          <div className="font-bebas text-3xl tracking-wide">PLAYSET</div>
        </Link>
      </div>

      {/* Quick actions row */}
      <div className="flex flex-row justify-between p-4 flex-wrap">
        <div className="flex flex-row gap-x-4 flex-wrap">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={requestScreenshot}
            style={{
              padding: "6px 10px",
              border: "1px solid #666",
              background: "#333",
              color: "#eee",
              cursor: "pointer",
            }}
            disabled={actionsDisabled}
            className="h-24 min-w-32 rounded-3xl font-semibold text-base font-nunito disabled:opacity-20 disabled:pointer-events-none"
          >
            <span className="text-2xl">📸</span>
            <br />
            Screenshot
          </button>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openQuickFeedback("comment")}
            style={{
              padding: "6px 10px",
              border: "1px solid #666",
              background: "#333",
              color: "#eee",
              cursor: "pointer",
            }}
            disabled={actionsDisabled}
            className="h-24 min-w-28 rounded-3xl font-semibold text-base font-nunito disabled:opacity-20 disabled:pointer-events-none"
          >
            <span className="text-2xl">💬</span>
            <br />
            Comment
          </button>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openQuickFeedback("bug")}
            style={{
              padding: "6px 10px",
              border: "1px solid #666",
              background: "#333",
              color: "#eee",
              cursor: "pointer",
            }}
            disabled={actionsDisabled}
            className="h-24 min-w-24 rounded-3xl font-semibold text-base font-nunito disabled:opacity-20 disabled:pointer-events-none"
          >
            <span className="text-2xl">🐞</span>
            <br />
            Bug
          </button>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => addQuickTag("😄 Fun")}
            style={{
              padding: "6px 10px",
              border: "1px solid #444",
              background: "#222",
              color: "#eee",
              cursor: "pointer",
            }}
            disabled={actionsDisabled}
            className="h-24 min-w-24 rounded-3xl font-semibold text-base font-nunito disabled:opacity-20 disabled:pointer-events-none"
          >
            <span className="text-2xl">😄</span>
            <br />
            Fun
          </button>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => addQuickTag("😐 Boring")}
            style={{
              padding: "6px 10px",
              border: "1px solid #444",
              background: "#222",
              color: "#eee",
              cursor: "pointer",
            }}
            disabled={actionsDisabled}
            className="h-24 min-w-24 rounded-3xl font-semibold text-base font-nunito disabled:opacity-20 disabled:pointer-events-none"
          >
            <span className="text-2xl">😐</span>
            <br />
            Boring
          </button>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => addQuickTag("❤️ Love")}
            style={{
              padding: "6px 10px",
              border: "1px solid #444",
              background: "#222",
              color: "#eee",
              cursor: "pointer",
            }}
            disabled={actionsDisabled}
            className="h-24 min-w-24 rounded-3xl font-semibold text-base font-nunito disabled:opacity-20 disabled:pointer-events-none"
          >
            <span className="text-2xl">❤️</span>
            <br />
            Love
          </button>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => addQuickTag("😡 Hate")}
            style={{
              padding: "6px 10px",
              border: "1px solid #444",
              background: "#222",
              color: "#eee",
              cursor: "pointer",
            }}
            disabled={actionsDisabled}
            className="h-24 min-w-24 rounded-3xl font-semibold text-base font-nunito disabled:opacity-20 disabled:pointer-events-none"
          >
            <span className="text-2xl">😡</span>
            <br />
            Hate
          </button>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={addDevMessage}
            style={{
              padding: "6px 10px",
              color: "#eee",
              cursor: "pointer",
            }}
            disabled={actionsDisabled}
            className="h-24 min-w-24 bg-[#8338ec]/20 border border-[#8338ec] rounded-3xl font-semibold text-base font-nunito disabled:opacity-20 disabled:pointer-events-none"
          >
            <span className="text-2xl">🧑‍💻</span>
            <br />
            Mock Dev Message
          </button>
        </div>

        <div className="flex flex-row gap-x-4 flex-wrap items-center">
          <div className="flex flex-col justify-center h-24 min-w-24 rounded-3xl font-semibold text-base font-nunito">
            <div className="text-xs font-semibold px-4 opacity-100 text-yellow-500">
              YOU&apos;RE PLAYING
            </div>
            <div className="text-xl font-semibold px-4">
              {displayGameTitle || "Untitled game"}
            </div>
            {displayDevName ? (
              <div className="text-sm px-4">
                <span className="text-xs opacity-100 text-yellow-500">BY</span>{" "}
                {displayDevName}
              </div>
            ) : null}

          </div>

          {/* time left pill (only if there is a limit) */}
          {usageLimitSeconds != null && (
            <div className="h-24 flex items-center">
              <div className="px-4 py-2 rounded-3xl border border-yellow-500/70 bg-yellow-500/10 text-sm font-nunito">
                <div className="text-[11px] uppercase tracking-wide text-yellow-400">
                  Time left
                </div>
                <div className="text-base font-semibold">
                  {timeRemainingSeconds != null
                    ? formatElapsed(timeRemainingSeconds)
                    : formatElapsed(
                      Math.max(0, usageLimitSeconds - usageUsedSecondsBefore)
                    )}
                </div>
                {requiresReadySignal && !gameReady && !timeLimitReached && (
                  <div className="mt-1 text-[10px] text-yellow-300/80">
                    Waiting for game to signal it&apos;s ready – timer is paused.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* <div className="flex items-center">
            <button
              type="button"
              onClick={runAccessDebug}
              disabled={debugLoading}
              className="ml-2 px-3 py-2 text-sm rounded bg-[#222] border border-neutral-700"
            >
              {debugLoading ? "Running…" : "Run access debug"}
            </button>
          </div> */}
        </div>
      </div>

      {/* Main row: game + timeline */}
      <div className="flex flex-row gap-4 px-4 pb-4 flex-1 min-h-0">
        {/* LEFT: game */}
        <div className="relative flex-[5] rounded-lg border border-neutral-800 overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-40 z-0"
            style={{
              zIndex: 5,
              backgroundImage:
                "radial-gradient(circle at center, #fff 0, transparent 100%)",
            }}
          />

          <div
            className="pointer-events-none absolute inset-0 opacity-20 z-0 blur-sm"
            style={{
              zIndex: 4,
              backgroundImage:
                'url("https://pub-95a88df110764e7787b2404f087418a7.r2.dev/pd_bg.png")',
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />

          {/* game fills all available space */}
          <div className="relative z-20 w-full h-full mx-auto">
            {resolving ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="animate-spin h-8 w-8 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              </div>
            ) : resolveError ? (
              <div className="absolute inset-0 flex items-center justify-center text-center px-4">
                <div>
                  <div className="text-sm text-red-300">{resolveError}</div>
                  <div className="text-xs text-gray-400 mt-2">
                    Ensure the game or build exists and is published, and that
                    you have access.
                  </div>
                </div>
              </div>
            ) : !accessGranted ? (
              <div className="absolute inset-0 flex items-center justify-center text-center px-4">
                <div className="bg-black/80 border border-[#333] rounded-xl p-4 space-y-3 max-w-sm mx-auto">
                  {!currentUser && !inviteInfo ? (
                    <>
                      <p className="text-sm text-[#eee]">
                        This is a private playtest. Please sign in with the email
                        address that was invited to access this game.
                      </p>
                      <p className="text-xs text-[#aaa]">
                        After signing in, open the invite from your Tester
                        Dashboard or this link again.
                      </p>
                    </>
                  ) : pendingInvite ? (
                    <>
                      <p className="text-sm text-[#eee]">
                        You&apos;ve been invited to test this game as{" "}
                        <span className="font-semibold">
                          {pendingInvite.email}
                        </span>
                        .
                      </p>
                      <p className="text-xs text-[#aaa]">
                        Accept to start playing, or decline if you don&apos;t want
                        to test this game.
                      </p>
                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={inlineDeclineInvite}
                          className="px-3 py-1.5 rounded border border-[#ef4444] text-[#ef4444] text-sm hover:bg-[#2b0a0a]"
                        >
                          Decline
                        </button>
                        <button
                          type="button"
                          onClick={inlineAcceptInvite}
                          className="px-3 py-1.5 rounded border border-[#10b981] text-[#10b981] text-sm hover:bg-[#022c22]"
                        >
                          Accept &amp; play
                        </button>
                      </div>
                      <p className="text-[11px] text-[#777]">
                        You can also manage this invite from your Tester
                        Dashboard.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-red-300">
                        You don&apos;t have access to this private game.
                      </p>
                      <p className="text-xs text-[#aaa]">
                        Make sure you are signed in with the invited email, or ask
                        the developer to invite you.
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                title="Playtest Game"
                src={entryUrl}
                className="absolute inset-0 w-full h-full border-none"
                allow="autoplay; fullscreen"
                tabIndex={-1}
                onLoad={() => {
                  setIframeReady(true);
                  focusGameCanvas();
                }}
              />
            )}

            {isDeveloperPreview &&
              accessGranted &&
              entryUrl &&
              !resolving &&
              !resolveError && (
                <div
                  className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 z-40
                             bg-black/80 border border-yellow-500/60 rounded-full px-4 py-2
                             text-[11px] text-yellow-200"
                >
                  Developer preview mode — reactions &amp; recording are disabled
                  (no time consumed).
                </div>
              )}

            {timeLimitReached && (
              <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center z-30 px-4 text-center">
                <h3 className="text-lg font-semibold mb-2">Time’s up</h3>
                <p className="text-sm text-gray-300 max-w-sm">
                  You’ve used all of your allocated time for this playtest. The
                  session has ended, but your feedback and recording are saved.
                </p>
              </div>
            )}

            {requiresReadySignal &&
              !isDeveloperPreview &&
              accessGranted &&
              entryUrl &&
              !gameReady &&
              !timeLimitReached && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/80 border border-yellow-500/60 rounded-full px-4 py-2 text-[11px] text-yellow-200 flex items-center gap-2 z-30">
                  <span className="inline-block w-2 h-2 rounded-full bg-yellow-300 animate-pulse" />
                  Waiting for the game to signal it&apos;s ready. Timer and
                  feedback are paused.
                </div>
              )}
          </div>

          {showForm && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
              <form
                onSubmit={handleSubmitFeedback}
                style={{
                  background: "#1e1e1e",
                  padding: "16px",
                  borderRadius: "8px",
                  width: "100%",
                  maxWidth: "420px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <h3 style={{ margin: 0 }}>Add feedback</h3>
                <p style={{ margin: 0, fontSize: "12px", opacity: 0.8 }}>
                  At {formatElapsed(getElapsedSeconds())} into this session
                </p>

                <label style={{ fontSize: "13px" }}>
                  Tag
                  <select
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    style={{
                      width: "100%",
                      marginTop: "4px",
                      padding: "6px",
                      borderRadius: "4px",
                      border: "1px solid #555",
                      background: "#111",
                      color: "#eee",
                    }}
                  >
                    <option value="bug">Bug</option>
                    <option value="confusing">Confusing</option>
                    <option value="fun">Fun</option>
                    <option value="idea">Idea</option>
                    <option value="comment">Comment</option>
                  </select>
                </label>

                <label style={{ fontSize: "13px" }}>
                  What’s happening?
                  <textarea
                    value={currentNote}
                    onChange={(e) => setCurrentNote(e.target.value)}
                    rows={4}
                    style={{
                      width: "100%",
                      marginTop: "4px",
                      padding: "6px",
                      borderRadius: "4px",
                      border: "1px solid #555",
                      background: "#111",
                      color: "#eee",
                      resize: "vertical",
                    }}
                    required
                  />
                </label>

                {formScreenshot && (
                  <div style={{ marginTop: "8px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      Screenshot captured for this feedback
                    </div>
                    <img
                      src={formScreenshot}
                      alt="Screenshot for this feedback"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "160px",
                        borderRadius: "4px",
                        border: "1px solid #444",
                        display: "block",
                      }}
                    />
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeFeedbackForm}
                    style={{
                      background: "transparent",
                      borderRadius: "4px",
                      padding: "6px 10px",
                      border: "1px solid #555",
                      color: "#eee",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      background: "#ffb300",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      border: "none",
                      color: "#000",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* RIGHT: feedback timeline */}
        <div className="flex-[2] rounded-lg border border-neutral-800 bg-[#181818] p-3 flex flex-col min_h-0 font-nunito">
          {debugError && (
            <div className="mb-2 text-sm text-red-400">
              Debug error: {debugError}
            </div>
          )}
          {debugResults && (
            <div className="mb-2 p-2 bg-[#0b0b0b] border border-gray-700 text-xs max-h-40 overflow-auto">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm">Debug results</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={copyDebugResults}
                    className="px-2 py-1 rounded border border-[#64748b] text-[#cbd5e1] text-xs"
                  >
                    Copy JSON
                  </button>
                  <button
                    onClick={downloadDebugResults}
                    className="px-2 py-1 rounded border border-[#64748b] text-[#cbd5e1] text-xs"
                  >
                    Download JSON
                  </button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap">
                {JSON.stringify(debugResults, null, 2)}
              </pre>
            </div>
          )}
          <h2 className="mt-0 text-lg font-semibold mb-2">Feedback Timeline</h2>

          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
            {isDeveloperPreview ? (
              <p style={{ opacity: 0.85, fontSize: "14px" }}>
                You’re viewing as the developer. Feedback, screenshots and
                recording are disabled here.
              </p>
            ) : events.length === 0 ? (
              <p style={{ opacity: 0.7, fontSize: "14px" }}>
                No feedback yet. <br />
                Press <code>F8</code>, or use the quick actions above.
              </p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {events
                  .slice()
                  .sort((a, b) => b.timeSeconds - a.timeSeconds)
                  .map((ev) => {
                    const isDevMessage = ev.isDevMessage;
                    const cardStyle = {
                      padding: "8px 10px",
                      marginBottom: "8px",
                      borderRadius: "6px",
                      background: isDevMessage ? "#8338ec" : "#222",
                      border: "1px solid #333",
                      fontSize: "14px",
                    };

                    return (
                      <li key={ev.id} style={cardStyle}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              textTransform: isDevMessage
                                ? "none"
                                : "capitalize",
                            }}
                          >
                            {isDevMessage ? "Message from the dev" : ev.tag}
                          </span>
                          <span style={{ opacity: 0.7 }}>
                            {formatElapsed(ev.timeSeconds)}
                          </span>
                        </div>

                        {ev.note && (
                          <div style={{ whiteSpace: "pre-wrap" }}>{ev.note}</div>
                        )}

                        {/* In this live play page, we still show the local dataUrl thumbnail immediately */}
                        {ev.screenshot && (
                          <div style={{ marginTop: "6px" }}>
                            <img
                              src={ev.screenshot}
                              alt={`Screenshot at ${formatElapsed(
                                ev.timeSeconds
                              )}`}
                              style={{
                                borderRadius: "4px",
                                border: "1px solid #444",
                                display: "block",
                              }}
                              className="w-24 h-24 object-cover"
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlaytestPage;
